
/*
 * CXD CART DRAWER
 * Opens automatically from the right after Add to Cart.
 * Preserves native Shopify cart functionality.
 */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', event => {
        if (event.code === 'Escape') {
          this.close();
        }
      });

      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
    }

    bindOverlay() {
      const overlay = this.querySelector(
        '#CartDrawer-Overlay'
      );

      if (
        !overlay ||
        overlay.dataset.cxdBound === 'true'
      ) return;

      overlay.addEventListener(
        'click',
        () => this.close()
      );

      overlay.dataset.cxdBound = 'true';
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector(
        '#cart-icon-bubble'
      );

      if (
        !cartLink ||
        cartLink.dataset.cxdDrawerBound === 'true'
      ) return;

      cartLink.setAttribute(
        'role',
        'button'
      );

      cartLink.setAttribute(
        'aria-haspopup',
        'dialog'
      );

      cartLink.setAttribute(
        'aria-controls',
        'CartDrawer'
      );

      cartLink.addEventListener(
        'click',
        event => {
          event.preventDefault();
          this.open(cartLink);
        }
      );

      cartLink.addEventListener(
        'keydown',
        event => {
          if (
            event.code === 'Space' ||
            event.code === 'SPACE' ||
            event.key === ' '
          ) {
            event.preventDefault();
            this.open(cartLink);
          }
        }
      );

      cartLink.dataset.cxdDrawerBound =
        'true';
    }

    open(triggeredBy) {
      if (triggeredBy) {
        this.setActiveElement(triggeredBy);
      }

      if (
        this.classList.contains('active')
      ) return;

      const cartDrawerNote =
        this.querySelector(
          '[id^="Details-"] summary'
        );

      if (
        cartDrawerNote &&
        !cartDrawerNote.hasAttribute('role')
      ) {
        this.setSummaryAccessibility(
          cartDrawerNote
        );
      }

      this.bindOverlay();

      this.classList.add('animate');

      document.body.classList.add(
        'overflow-hidden'
      );

      requestAnimationFrame(() => {
        this.classList.add('active');
      });

      const panel = this.querySelector(
        '.drawer__inner'
      );

      if (panel) {
        const focusWhenOpen = () => {
          if (
            !this.classList.contains('active')
          ) return;

          const focusContainer =
            this.classList.contains('is-empty')
              ? (
                  this.querySelector(
                    '.drawer__inner-empty'
                  ) || panel
                )
              : (
                  this.querySelector(
                    '#CartDrawer'
                  ) || panel
                );

          if (
            typeof trapFocus === 'function'
          ) {
            trapFocus(
              focusContainer,
              panel
            );
          }
        };

        const finish = event => {
          if (event.target !== panel) return;

          panel.removeEventListener(
            'transitionend',
            finish
          );

          focusWhenOpen();
        };

        panel.addEventListener(
          'transitionend',
          finish
        );

        setTimeout(() => {
          panel.removeEventListener(
            'transitionend',
            finish
          );

          if (
            this.classList.contains('active') &&
            !panel.contains(
              document.activeElement
            )
          ) {
            focusWhenOpen();
          }
        }, 430);
      }

      this.querySelector(
        'cart-drawer-items'
      )?.dispatchViewEvent?.();
    }

    close() {
      this.classList.remove('active');

      if (
        typeof removeTrapFocus === 'function'
      ) {
        removeTrapFocus(
          this.activeElement
        );
      }

      document.body.classList.remove(
        'overflow-hidden'
      );
    }

    setSummaryAccessibility(summary) {
      summary.setAttribute(
        'role',
        'button'
      );

      summary.setAttribute(
        'aria-expanded',
        'false'
      );

      if (summary.nextElementSibling?.id) {
        summary.setAttribute(
          'aria-controls',
          summary.nextElementSibling.id
        );
      }

      summary.addEventListener(
        'click',
        event => {
          const details =
            event.currentTarget.closest(
              'details'
            );

          event.currentTarget.setAttribute(
            'aria-expanded',
            String(
              !details?.hasAttribute('open')
            )
          );
        }
      );

      if (
        typeof onKeyUpEscape === 'function'
      ) {
        summary.parentElement
          ?.addEventListener(
            'keyup',
            onKeyUpEscape
          );
      }
    }

    renderContents(parsedState) {
      this.productId = parsedState.id;

      if (!parsedState?.sections) {
        this.open();
        return;
      }

      this.getSectionsToRender().forEach(
        section => {
          const sectionHTML =
            parsedState.sections[
              section.id
            ];

          if (!sectionHTML) return;

          const target = section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

          if (!target) return;

          const source = this.getSectionDOM(
            sectionHTML,
            section.selector ||
              '.shopify-section'
          );

          if (source) {
            target.innerHTML =
              source.innerHTML;
          }
        }
      );

      this.bindOverlay();

      this.setHeaderCartIconAccessibility();

      this.classList.remove('is-empty');

      this.querySelector(
        '.drawer__inner'
      )?.classList.remove('is-empty');

      requestAnimationFrame(() => {
        this.open();
      });
    }

    getSectionInnerHTML(
      html,
      selector = '.shopify-section'
    ) {
      return (
        this.getSectionDOM(
          html,
          selector
        )?.innerHTML || ''
      );
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer'
        },
        {
          id: 'cart-icon-bubble'
        }
      ];
    }

    getSectionDOM(
      html,
      selector = '.shopify-section'
    ) {
      if (!html) return null;

      return new DOMParser()
        .parseFromString(
          html,
          'text/html'
        )
        .querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

if (!customElements.get('cart-drawer-items')) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }
  }

  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}
